import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  CardMedia,
  Collapse,
  Fade,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { EGButton } from "../../../../../MainComponents/Button/EGButton/EGButton";
import { useTranslation } from "react-i18next";
import { EGIconButton } from "../../../../../MainComponents/Button/IconButton/IconButton";
import { useSnackbar } from "notistack";
import { UnsavedChanges } from "../../../../../Modal/Confirmation/UnsavedChanges/UnsavedChanges";
import { FinishConfirm } from "./FinishConfirm";
import { updateInterview, createAttachment } from "../../../../../../apis";
import EthiquinNoData from "../../../../../../assets/images/Ethiquin/ethiquin_no_data.svg";
import useUnsavedChangesWarning from "../../../../../../hooks/useUnsavedChangesWarning";
import { useOfflineSync } from "../../../../../../hooks/useOfflineSync";
import { AudioRecorderControls } from "./AudioRecorderControls";
import { OfflineSyncIndicator } from "./OfflineSyncIndicator";

const AUDIO_EXTENSIONS = [
  "mp3",
  "wav",
  "flac",
  "aac",
  "m4a",
  "wma",
  "ogg",
  "alac",
  "mpeg",
  "webm",
];

const isAudioAttachment = (att) => {
  if (!att) return false;
  if (att?.attachments_data?.isInterviewRecording) return true;
  const type = att?.attachments_data?.type || "";
  if (type.startsWith("audio/")) return true;
  const name = att?.attachments_data?.name || "";
  const ext = name.split(".").pop()?.toLowerCase();
  return AUDIO_EXTENSIONS.includes(ext);
};

export const ApplyQnA = forwardRef(
  (
    {
      goHome,
      interview = {},
      evidence =[],
      setInterviews,
      isMobil,
      isLoading,
      setIsLoading,
      id,
      alias,
      audioRecorder,
    },
    ref
  ) => {
    // ---> Custom hooks <--- //
    const [t] = useTranslation("investigation");
    const theme = useTheme();
    const offlineSync = useOfflineSync();

    const initialQuestionsRef = useRef([]);
    const inputRefs = useRef({});
    const paperRefs = useRef({});
    const prevDetailsRef = useRef({});
    const pendingFocusRef = useRef(null);
    const rootRef = useRef(null);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const normalizeQuestions = (qs = []) =>
      qs.map((q) => ({
        ...q,
        _showDetails:
          q._showDetails ??
          (typeof q.answerDetails === "string" && q.answerDetails.length > 0),
        _isNew: q._isNew ?? false,
      }));

    // ---> Initial states <--- //
    const [questions, setQuestions] = useState(
      normalizeQuestions(interview?.interview || [])
    );
    const [activeIndex, setActiveIndex] = useState(null);
    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const [openFinishDialog, setOpenFinishDialog] = useState(false);
    const [openDetailsIdx, setOpenDetailsIdx] = useState(null);
    // Answer details
    const hdlToggleDetails = (i) => {
      setOpenDetailsIdx((prev) => (prev === i ? null : i));
    };
    // Initial questions
    useEffect(() => {
      const normalized = normalizeQuestions(interview?.interview || []);
      setQuestions(normalized);
      initialQuestionsRef.current = prepareQuestionsForSave(normalized);
    }, [interview]);

    // Hydrate the recorder list with audios already saved on the server,
    // so the interviewer can listen to context before recording more.
    // Re-runs when evidence changes (e.g., after a successful save).
    const setLoadedAudios = audioRecorder?.setLoadedAudios;
    useEffect(() => {
      if (!setLoadedAudios) return;

      const items = (Array.isArray(evidence) ? evidence : [])
        .filter((e) => isAudioAttachment(e))
        .map((e, idx) => ({
          id: `loaded-${e.attachments_file || idx}`,
          url: e.file_url,
          duration: Number(e?.attachments_data?.duration) || 0,
          source: "loaded",
          attachments_file: e.attachments_file,
          attachments_data: e.attachments_data,
          name: e?.attachments_data?.name,
        }));

      setLoadedAudios(items);
    }, [evidence, setLoadedAudios]);

    // Refresh offline-pending list only when the queue grows (new offline
    // save). Decreases (sync completed) are handled by promotePendingToLoaded
    // below, which preserves the in-memory Blob URL so playback never breaks.
    const refreshOfflineAudios = audioRecorder?.refreshOfflineAudios;
    const prevPendingCountRef = useRef(offlineSync.pendingCount);
    useEffect(() => {
      const prev = prevPendingCountRef.current;
      prevPendingCountRef.current = offlineSync.pendingCount;
      if (!refreshOfflineAudios) return;
      if (offlineSync.pendingCount > prev) {
        refreshOfflineAudios();
      }
    }, [offlineSync.pendingCount, refreshOfflineAudios]);

    // When a submission for THIS interview syncs, swap its pending entries
    // into the "saved" list silently — no fresh GET needed.
    const promotePendingToLoaded = audioRecorder?.promotePendingToLoaded;
    useEffect(() => {
      const event = offlineSync.lastSyncEvent;
      if (!event || !promotePendingToLoaded) return;
      if (event.interviewId !== id || event.alias !== alias) return;
      promotePendingToLoaded(event.submissionId, event.uploadedAudios);
    }, [
      offlineSync.lastSyncEvent,
      id,
      alias,
      promotePendingToLoaded,
    ]);

    useEffect(() => {
      const handlePointerDown = (e) => {
        const target = e.target;
        const root = rootRef.current;
        if (!root) return;
        // If the click/tap was outside the container -> deselect
        if (!root.contains(target)) {
          setActiveIndex(null);
          return;
        }
        // If the click/tap was inside the container but NOT inside any of the Papers -> deselect
        const papers = Object.values(paperRefs.current).filter(Boolean);
        const clickedPaper = papers.some((p) => p.contains(target));
        if (!clickedPaper) {
          setActiveIndex(null);
        }
      };
      // Deselect by pressing ESC
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          setActiveIndex(null);
        }
      };
      document.addEventListener("pointerdown", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("pointerdown", handlePointerDown);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, []);
    // When add a new question, useLayoutEffect will scroll+focus to the specified element
    useLayoutEffect(() => {
      const idx = pendingFocusRef.current;
      if (typeof idx === "number") {
        const paperEl = paperRefs.current[`paper-${idx}`];
        if (paperEl?.scrollIntoView) {
          try {
            paperEl.scrollIntoView({ behavior: "smooth", block: "center" });
          } catch (err) {
            // fallback
            paperEl.scrollIntoView();
          }
        }
        setActiveIndex(idx);
        // Wait for the next frame so that the input refs are bound in the DOM
        requestAnimationFrame(() => {
          // fallback to the response input whose id is `q-${idx}`
          const qInput =
            inputRefs.current[`q-question-${idx}`] ??
            inputRefs.current[`q-${idx}`]; // answer input
          qInput?.focus?.();
          // Clean pending
          pendingFocusRef.current = null;
        });
      }
    }, [questions.length]);
    // Input onChange
    const handleChange = (idx, field, value) => {
      const update = [...questions];
      update[idx] = { ...update[idx], [field]: value };
      setQuestions(update);
    };
    // Activate and focus
    const hdlActivateAndFocusQuestion = (i) => {
      setActiveIndex(i);
      setTimeout(() => {
        const paperEl = paperRefs.current[`paper-${i}`];
        const activeEl = document.activeElement;
        // If the active element is already inside the Paper -> do not sabotage it
        if (
          paperEl &&
          activeEl instanceof Element &&
          paperEl.contains(activeEl) &&
          (activeEl.tagName === "INPUT" ||
            activeEl.tagName === "TEXTAREA" ||
            activeEl.isContentEditable)
        ) {
          return;
        }
        // prefer question input if it exists, otherwise fallback to answer input
        const qEl =
          inputRefs.current[`q-question-${i}`] ?? inputRefs.current[`q-${i}`];
        qEl?.focus?.();
      }, 0);
    };
    // toggle show/hide input comments (answerDetails)
    const toggleAnswerObs = useCallback(
      (i) => {
        setQuestions((prev) => {
          const update = [...prev];
          const current = update[i] || {};
          const hasDetails =
            typeof current.answerDetails === "string" &&
            current.answerDetails.length > 0;
          const isShown = !!current._showDetails;

          if (isShown) {
            // hidden: if there was text, it is temporarily saved for “undo”
            if (hasDetails) {
              prevDetailsRef.current[i] = current.answerDetails;
            } else {
              delete prevDetailsRef.current[i];
            }
            update[i] = {
              ...current,
              _showDetails: false,
              answerDetails: undefined,
            };
          } else {
            // Display: initialize if it doesn't exist.
            update[i] = {
              ...current,
              _showDetails: true,
              answerDetails: current.answerDetails ?? "",
            };
          }

          return update;
        });
        // on focus to details field when it opens (wait for the next render)
        setTimeout(() => {
          inputRefs.current[`q-details-${i}`]?.focus?.();
        }, 0);
        //  If you just hid something and there was text, show a snackbar with an “Undo” action
        setTimeout(() => {
          const prevVal = prevDetailsRef.current[i];
          if (typeof prevVal === "string" && prevVal.length > 0) {
            enqueueSnackbar(t("Interview.comments_deleted"), {
              variant: "info",
              ...(isMobil
                ? { anchorOrigin: { vertical: "top", horizontal: "center" } }
                : {}),
              action: (snackKey) => (
                <EGButton
                  label={t("Interview.undo")}
                  color="info"
                  variant="outlined"
                  borderColor="white"
                  size="small"
                  onClick={() => {
                    setQuestions((prevQs) => {
                      const restored = [...prevQs];
                      restored[i] = {
                        ...(restored[i] || {}),
                        _showDetails: true,
                        answerDetails: prevVal,
                      };
                      return restored;
                    });
                    delete prevDetailsRef.current[i];
                    closeSnackbar(snackKey);
                    setTimeout(() => {
                      inputRefs.current[`q-details-${i}`]?.focus?.();
                    }, 50);
                  }}
                />
              ),
            });
          }
        }, 100);
      },
      [enqueueSnackbar, closeSnackbar, t]
    );

    // Add new question (avoid empty duplicates)
    const addNewQuestion = useCallback(() => {
      // If there is any question whose “question” field is empty, you are not allowed to add another one
      const hasEmptyQuestion = questions.some(
        (q) => !(q.question && q.question.toString().trim().length > 0)
      );
      if (hasEmptyQuestion) {
        enqueueSnackbar(
          t("Interview.complete_empty_question_before_adding_new_one"),
          {
            ...(isMobil
              ? { anchorOrigin: { vertical: "top", horizontal: "center" } }
              : {}),
            variant: "warning",
          }
        );
        return;
      }
      const newQ = {
        question: "",
        answer: "",
        answerDetails: "",
        _isNew: true,
        _showDetails: false,
      };
      setQuestions((prev) => {
        const next = [...prev, newQ];
        // set the focus for the index that will have the new element
        pendingFocusRef.current = next.length - 1;
        return next;
      });
    }, [questions, enqueueSnackbar]);
    // Delete question (only new ones)
    const deleteQuestion = useCallback(
      (i) => {
        const removed = questions[i];
        // It is determined whether the item had meaningful content
        const hadContent =
          (removed?.question &&
            removed.question.toString().trim().length > 0) ||
          (removed?.answer && removed.answer.toString().trim().length > 0) ||
          (removed?.answerDetails &&
            removed.answerDetails.toString().trim().length > 0);
        // Always remove the question from the array
        setQuestions((prev) => prev.filter((_, idx) => idx !== i));
        // Snackbar is only shown if there was content
        if (!hadContent) return;
        enqueueSnackbar(t("Interview.question_deleted"), {
          variant: "warning",
          preventDuplicate: true,
          ...(isMobil
            ? { anchorOrigin: { vertical: "top", horizontal: "center" } }
            : {}),
          action: (snackKey) => (
            <EGButton
              label={t("Interview.undo")}
              color="warning"
              variant="outlined"
              borderColor="white"
              onClick={() => {
                setQuestions((prev) => {
                  const u = [...prev];
                  // if there is at least one empty row (without question), it is replaced
                  const emptyIdx = u.findIndex(
                    (q) =>
                      !(q.question && q.question.toString().trim().length > 0)
                  );
                  if (emptyIdx !== -1) {
                    u[emptyIdx] = removed; // Replaces the first empty one
                  } else {
                    // if the original index is greater than the current length, push
                    if (i <= u.length) {
                      u.splice(i, 0, removed); // restore to the same position if possible
                    } else {
                      u.push(removed); // fallback: insert
                    }
                  }
                  return u;
                });
                closeSnackbar(snackKey);
                setTimeout(() => {
                  focusQuestionAtIndex(i);
                }, 50);
              }}
            />
          ),
        });
      },
      [questions, enqueueSnackbar, closeSnackbar, t]
    );

    const prepareQuestionsForSave = useCallback(() => {
      return (
        questions
          .map((q) => {
            // Copy only the “public” keys (not those beginning with _)
            const cleaned = Object.keys(q).reduce((acc, key) => {
              if (key.startsWith("_")) return acc;
              const val = q[key];
              if (typeof val === "undefined") return acc; // don't include undefined
              acc[key] = val;
              return acc;
            }, {});

            // Normalize text fields and trim
            if (typeof cleaned.question === "string") {
              cleaned.question = cleaned.question.trim();
              if (cleaned.question === "") delete cleaned.question;
            }

            if (typeof cleaned.answer === "string") {
              cleaned.answer = cleaned.answer.trim();
            }

            if (typeof cleaned.answerDetails === "string") {
              cleaned.answerDetails = cleaned.answerDetails.trim();
              if (cleaned.answerDetails === "") delete cleaned.answerDetails;
            }

            // Ensure that if there is a question (i.e. this row is a real Q),
            // Always include the 'answer' key (use empty string when not provided).
            // This guarantees answer: "" will be sent instead of omitting the key.
            if (
              typeof cleaned.question === "string" &&
              cleaned.question.length > 0
            ) {
              if (!Object.prototype.hasOwnProperty.call(cleaned, "answer")) {
                cleaned.answer = "";
              } else if (cleaned.answer === undefined) {
                cleaned.answer = "";
              } // if answer is present and trimmed to "", keep it as ""
            } else {
              // If there's no question text, we don't force an answer key.
              // Also if answer is empty (or only whitespace) and there's no question,
              // we should remove it to avoid sending meaningless entries.
              if (typeof cleaned.answer === "string" && cleaned.answer === "") {
                delete cleaned.answer;
              }
            }

            return cleaned;
          })
          // filter objects that were left completely “empty” (without public keys)
          .filter((c) => Object.keys(c).length > 0)
      );
    }, [questions]);

    // helper: focus + scroll to question in index i (use pendingFocusRef as fallback)
    const focusQuestionAtIndex = (i) => {
      // Set activeIndex so that the input exists if necessary
      setActiveIndex(i);
      // Wait for the next frame so that the input is assembled
      requestAnimationFrame(() => {
        const paperEl = paperRefs.current[`paper-${i}`];
        if (paperEl?.scrollIntoView) {
          try {
            paperEl.scrollIntoView({ behavior: "smooth", block: "center" });
          } catch {
            paperEl.scrollIntoView();
          }
        }
        const qInput = inputRefs.current[`q-question-${i}`];
        if (qInput?.focus) {
          qInput.focus();
        } else {
          // fallback: If it doesn't exist yet
          pendingFocusRef.current = i;
        }
      });
    };
    // Auxiliary function for formatting the index list
    const formatIndexes = (
      indexes,
      conjunction = "and",
      useOxfordComma = false
    ) => {
      const withHashes = indexes.map((i) => `#${i + 1}`);

      if (withHashes.length === 1) return withHashes[0];
      if (withHashes.length === 2) return withHashes.join(` ${conjunction} `);
      //  For lists of 3 or more items
      const last = withHashes.pop();
      const comma = useOxfordComma ? "," : "";
      return `${withHashes.join(", ")}${comma} ${conjunction} ${last}`;
    };

    // Returns array of invalid indexes (without question but with answer or details)
    const findInvalidQuestionIndexes = useCallback(() => {
      const invalid = [];
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i] || {};
        const hasQuestion =
          typeof q.question === "string" && q.question.trim().length > 0;
        const hasAnswer =
          typeof q.answer === "string" && q.answer.trim().length > 0;
        const hasDetails =
          typeof q.answerDetails === "string" &&
          q.answerDetails.trim().length > 0;

        if (!hasQuestion && (hasAnswer || hasDetails)) {
          invalid.push(i);
        }
      }
      return invalid;
    }, [questions]);

    // Build message based on quantity (use i18n)
    const buildInvalidQuestionsMessage = (invalidIdxs) => {
      if (!invalidIdxs || invalidIdxs.length === 0) return "";

      if (invalidIdxs.length === 1) {
        // Ej: "La pregunta #3 está incompleta: le falta el texto."
        return `${t("Interview.the_question")} #${invalidIdxs[0] + 1} ${t(
          "Interview.is_incomplete_question_text"
        )}`;
      }
      // plural: "Las preguntas #2, #4 y #7 están incompletas: les falta el texto."
      const pretty = formatIndexes(invalidIdxs, t("Interview.and"), true);
      return `${t("Interview.the_questions")} ${pretty} ${t(
        "Interview.are_incomplete_no_question_text"
      )}`;
    };
    // On submit
    const handleSubmit = async (status = "process") => {
      const invalidIdxs = findInvalidQuestionIndexes();

      if (invalidIdxs.length > 0) {
        const message = buildInvalidQuestionsMessage(invalidIdxs);

        enqueueSnackbar(message, {
          variant: "warning",
        });
        focusQuestionAtIndex(invalidIdxs[0]);
        return;
      }

      // if there are no invalid entries, continue with normal delivery
      setIsLoading(true);
      const loading = enqueueSnackbar(`${t("Interview.saving")}...`, {
        variant: "info",
        persist: true,
      });

      try {
        // Stop recording if still active
        if (audioRecorder?.isRecording) {
          await audioRecorder.stopRecording(true);
        }

        // Each recorded audio is uploaded as its own attachment.
        // Concatenating multiple WebM/Opus sessions produces an invalid container
        // that some players truncate after the first segment, so we keep them
        // separate end-to-end.
        const audios = (audioRecorder?.recordedAudios || []).map((a) => ({
          blob: a.blob,
          duration: a.duration,
        }));

        // Existing attachments: keep non-audio evidence as-is, but for audios
        // honor the user's deletions by only sending what's still in
        // audioRecorder.loadedAudios (which mirrors what's visible in the list).
        const evidenceList = Array.isArray(evidence) ? evidence : [];
        const nonAudioAttachments = evidenceList
          .filter((e) => !isAudioAttachment(e))
          .map((e) => ({
            attachments_file: e.attachments_file,
            attachments_data: e.attachments_data,
          }));
        const keptAudioAttachments = (audioRecorder?.loadedAudios || []).map(
          (a) => ({
            attachments_file: a.attachments_file,
            attachments_data: a.attachments_data,
          }),
        );
        const existingAttachments = [
          ...nonAudioAttachments,
          ...keptAudioAttachments,
        ];

        // Build data to send (without audio attachments - those will be handled separately)
        const dataToSave = {
          alias,
          interviews_id: id,
          interviews_data: {
            status,
            interview: prepareQuestionsForSave(),
            ...(existingAttachments.length > 0 && {
              attachments: existingAttachments,
            }),
          },
        };

        // Helper: upload a single audio recording as its own attachment.
        const uploadOneAudio = async (audio, index) => {
          const blob = audio.blob;
          const timestamp = Date.now();
          const fileName = `interview_audio_${id}_${timestamp}_${index + 1}.webm`;
          const audioFile = new File([blob], fileName, {
            type: blob.type || "audio/webm",
          });
          const formData = new FormData();
          formData.append("attachments", audioFile);

          const uploadResp = await createAttachment(formData);

          const attData = {
            name: fileName,
            size: blob.size,
            type: blob.type || "audio/webm",
            isInterviewRecording: true,
          };
          if (audio.duration != null) attData.duration = audio.duration;

          return {
            attachments_file: uploadResp.attachments_id,
            attachments_data: attData,
          };
        };

        // Offline: queue everything as pending audios; processSubmission will upload them on sync.
        if (!navigator.onLine) {
          await offlineSync.addToQueue({
            type: "interview",
            interviewId: id,
            alias,
            payload: dataToSave,
            pendingAudios: audios,
          });

          if (audioRecorder?.clearAllRecordings) {
            await audioRecorder.clearAllRecordings();
          }

          enqueueSnackbar(t("Interview.saved_offline_will_sync"), {
            variant: "info",
          });
          goHome();
          return;
        }

        // Online: upload audios one by one so a single failure doesn't lose the others.
        const uploadedAudios = [];
        const failedAudios = [];
        for (let i = 0; i < audios.length; i++) {
          try {
            const att = await uploadOneAudio(audios[i], i);
            uploadedAudios.push(att);
          } catch (uploadErr) {
            console.error(`Audio ${i + 1} upload failed:`, uploadErr);
            failedAudios.push(audios[i]);
          }
        }

        // Merge successfully uploaded audios into the payload's attachments.
        if (uploadedAudios.length > 0) {
          const currentAttachments =
            dataToSave.interviews_data.attachments || [];
          dataToSave.interviews_data.attachments = [
            ...currentAttachments,
            ...uploadedAudios,
          ].reduce((acc, item) => {
            if (!acc.find((i) => i.attachments_file === item.attachments_file)) {
              acc.push(item);
            }
            return acc;
          }, []);
        }

        try {
          const resp = await updateInterview(dataToSave);
          setInterviews(resp.data.interviews);

          // Interview saved. If some audios failed to upload, queue them as a
          // follow-up update so the sync job links them later — without losing
          // the recordings the user already made.
          if (failedAudios.length > 0) {
            await offlineSync.addToQueue({
              type: "interview",
              interviewId: id,
              alias,
              payload: {
                alias,
                interviews_id: id,
                interviews_data: {
                  attachments: dataToSave.interviews_data.attachments || [],
                },
              },
              pendingAudios: failedAudios,
            });
            enqueueSnackbar(t("Interview.audios_pending_sync"), {
              variant: "warning",
            });
          }

          if (audioRecorder?.clearAllRecordings) {
            await audioRecorder.clearAllRecordings();
          }

          enqueueSnackbar(
            status === "process"
              ? t("Interview.progress_has_been_saved_successfully")
              : t("Interview.the_interview_has_been_completed_successfully"),
            {
              variant: "success",
            }
          );
          goHome();
        } catch (networkError) {
          // Interview save failed AFTER some audios may have already been
          // uploaded. Pass the uploaded ones as `preuploadedAudioAttachments`
          // so the sync job links them instead of re-uploading (would create
          // duplicate orphan files in S3).
          console.error("Network error, saving to queue:", networkError);

          await offlineSync.addToQueue({
            type: "interview",
            interviewId: id,
            alias,
            payload: dataToSave,
            pendingAudios: failedAudios,
            preuploadedAudioAttachments: uploadedAudios,
          });

          if (audioRecorder?.clearAllRecordings) {
            await audioRecorder.clearAllRecordings();
          }

          enqueueSnackbar(t("Interview.saved_offline_will_sync"), {
            variant: "info",
          });
          goHome();
        }
      } catch (err) {
        console.error(err);
        enqueueSnackbar(t("Interview.error_uploading_files"), {
          variant: "error",
        });
      } finally {
        setIsLoading(false);
        closeSnackbar(loading);
      }
    };
    // Determine if there are any unsaved changes
    const hasUnsavedChanges = useCallback(() => {
      const current = prepareQuestionsForSave(questions);
      const initial = initialQuestionsRef.current;
      const questionsChanged =
        JSON.stringify(current) !== JSON.stringify(initial);
      const hasRecordings = audioRecorder?.hasUnsavedRecordings || false;
      return questionsChanged || hasRecordings;
    }, [questions, prepareQuestionsForSave, audioRecorder?.hasUnsavedRecordings]);

    const handleGoHome = () => {
      if (hasUnsavedChanges()) {
        setOpenCancelDialog(true);
      } else {
        goHome(); // If there are no changes, go directly back
      }
    };
    // Cleaning up to return to the beginning
    const resetQuestions = useCallback(async () => {
      setQuestions(normalizeQuestions(interview?.interview || []));
      initialQuestionsRef.current = prepareQuestionsForSave(
        normalizeQuestions(interview?.interview || [])
      );
      // Also clear any recorded audio
      if (audioRecorder?.clearAllRecordings) {
        await audioRecorder.clearAllRecordings();
      }
    }, [interview, prepareQuestionsForSave, audioRecorder]);

    useUnsavedChangesWarning(!!hasUnsavedChanges());

    // Button actions
    const QuestionActions = ({ question, i, isMobil }) => {
      const deleteBtn = question._isNew && (
        <EGIconButton
          title={t("Interview.delete_question")}
          iconName="trash_can"
          iconColor="error"
          iconSize={22}
          onClick={(e) => {
            e.stopPropagation();
            deleteQuestion(i);
          }}
        />
      );

      const commentsBtn = (
        <EGIconButton
          title={
            question._showDetails
              ? t("Interview.remove_comments")
              : t("Interview.add_comments")
          }
          iconColor={question._showDetails ? "pinkIII" : "teal"}
          iconName={
            question._showDetails
              ? "outline_message_remove"
              : "outline_message_add_2"
          }
          iconSize={21}
          onClick={(e) => {
            e.stopPropagation();
            toggleAnswerObs(i);
          }}
        />
      );

      // Cambiar el orden dependiendo de isMobil
      return isMobil ? (
        <>
          {deleteBtn}
          {commentsBtn}
        </>
      ) : (
        <>
          {commentsBtn}
          {deleteBtn}
        </>
      );
    };

    useImperativeHandle(
      ref,
      () => ({
        addNewQuestion,
        handleGoHome,
        saveAsProcess: async () => {
          if (audioRecorder?.isRecording) {
            await audioRecorder.stopRecording(true);
          }
          await handleSubmit("process");
        },
        openFinishModal: () => {
          setOpenFinishDialog(true);
        },
        getState: () => ({
          questions,
          activeIndex,
        }),
        toggleAnswerObs,
        // Audio recording controls exposed to parent
        isRecording: audioRecorder?.isRecording || false,
        isPaused: audioRecorder?.isPaused || false,
        isRecordingSupported: audioRecorder?.isSupported || false,
        recordingDuration: audioRecorder?.duration || 0,
        hasRecordings: (audioRecorder?.recordedAudios?.length || 0) > 0,
        toggleRecording: audioRecorder?.toggleRecording,
        startRecording: audioRecorder?.startRecording,
        pauseRecording: audioRecorder?.pauseRecording,
        resumeRecording: audioRecorder?.resumeRecording,
        stopRecording: () => audioRecorder?.stopRecording?.(true),
        formatDuration: audioRecorder?.formatDuration,
      }),
      [
        addNewQuestion,
        handleGoHome,
        handleSubmit,
        setOpenFinishDialog,
        questions,
        activeIndex,
        toggleAnswerObs,
        audioRecorder,
      ]
    );

    return (
      <>
        <Box
          ref={rootRef}
          sx={{
            px: 1,
            py: 1.25,
            width: "100%",
            maxWidth: 800,
            mx: "auto",
            borderRadius: 1,
            pb: 1.5,
            mb: { md: 1 },
          }}
        >
          {/* Offline Sync Indicator */}
          <Box sx={{ maxWidth: { xs: "100%", md: "93%" } }}>
            <OfflineSyncIndicator
              isOnline={offlineSync.isOnline}
              isSyncing={offlineSync.isSyncing}
              pendingCount={offlineSync.pendingCount}
              onSync={offlineSync.triggerSync}
              disabled={isLoading}
            />
          </Box>

          {/* Audio Recording Controls */}
          {audioRecorder?.isSupported && (
            <Box sx={{ maxWidth: { xs: "100%", md: "93%" }, mb: 1 }}>
              <AudioRecorderControls
                audioRecorder={audioRecorder}
                isMobil={isMobil}
                disabled={isLoading}
              />
            </Box>
          )}

          {/* Interview objetives */}
          {!!interview.details && (
            <Paper
              sx={{
                maxWidth: { xs: "100%", md: "93%" },
                py: 2,
                px: 2.5,
                mb: 1.5,
              }}
              elevation={1}
            >
              <Typography variant="h5" color="primary.dark">
                {t("Interview.interview_objective")}:
              </Typography>
              <Typography
                variant="body1"
                textAlign="justify"
                sx={{ whiteSpace: "pre-line", px: 0.5, mt: 1 }}
              >
                {interview.details}
              </Typography>
            </Paper>
          )}
          {/* Questions and Answers */}
          {questions.length === 0 ? (
            <Paper
              sx={{
                maxWidth: { xs: "100%", md: "93%" },
                p: 2,
                my: 3,
                bgcolor: "transparent",
              }}
              elevation={0}
            >
              <Typography textAlign="center" variant="h4">
                {t("Interview.no_questions_available")}...
              </Typography>
              <CardMedia
                src={EthiquinNoData}
                component="img"
                alt="No data"
                sx={{
                  width: { xs: "70%", md: 400 },
                  height: "auto",
                  mx: "auto",
                  my: 2,
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  msUserSelect: "none",
                  WebkitTouchCallout: "none",
                  WebkitUserDrag: "none",
                }}
              />
            </Paper>
          ) : (
            <>
              {questions.map((question, i) => (
                <Paper
                  key={i}
                  ref={(el) => (paperRefs.current[`paper-${i}`] = el)}
                  sx={{
                    maxWidth: { xs: "100%", md: "93%" },
                    py: 2,
                    px: 2.5,
                    mb: 1.5,
                    cursor: "pointer",
                    position: "relative",
                    transition: "border-color 0.3s ease",
                    borderLeft:
                      activeIndex === i &&
                      `6px solid ${
                        question._isNew
                          ? theme.palette.success.main
                          : theme.palette.primary.main
                      }`,
                  }}
                  onClick={(e) => hdlActivateAndFocusQuestion(i)}
                >
                  <Box>
                    {(() => {
                      const raw = question.question ?? "";
                      const hasText = raw.toString().trim().length > 2;
                      // Show input if there is NO text (empty field)
                      // or if it is new and active (explicit edit mode)
                      const showInput =
                        !hasText || (question._isNew && activeIndex === i);

                      if (showInput) {
                        return (
                          <TextField
                            id={`q-question-${i}`}
                            inputRef={(el) =>
                              (inputRefs.current[`q-question-${i}`] = el)
                            }
                            label={`${t("Interview.question")} #${i + 1}:`}
                            variant="filled"
                            multiline
                            minRows={1}
                            maxRows={6}
                            fullWidth
                            value={question.question ?? ""}
                            onFocus={() => setActiveIndex(i)}
                            onBlur={(e) => {
                              // trims when losing focus
                              const trimmed = e.target.value.toString().trim();
                              if (trimmed !== (question.question ?? "")) {
                                handleChange(i, "question", trimmed);
                              }
                            }}
                            disabled={isLoading}
                            onChange={(e) =>
                              handleChange(i, "question", e.target.value)
                            }
                            sx={{
                              "& .MuiInputBase-input": { fontWeight: 600 },
                              "& .MuiFilledInput-root.Mui-focused": {
                                backgroundColor: theme.palette.blueGray50.main,
                              },
                              "& .MuiInputLabel-shrink": {
                                fontSize: "1.05rem",
                              },
                            }}
                          />
                        );
                      }
                      // if there is text and it is not in edit mode, display Typography
                      return (
                        <Typography
                          variant="h6"
                          sx={{ display: "inline" }}
                          textAlign="justify"
                        >
                          {`#${i + 1}. `} {question.question}
                        </Typography>
                      );
                    })()}
                  </Box>
                  {/* Show/hide Link */}
                  {question.details && (
                    <Typography textAlign="right" mb={0.5} mr={0.5}>
                      <Link
                        id={`objetive-toggle-${i}`}
                        component="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          hdlToggleDetails(i);
                        }}
                        underline="hover"
                        marginLeft="auto"
                        aria-expanded={openDetailsIdx === i}
                        aria-controls={`details-panel-${i}`}
                      >
                        {openDetailsIdx === i
                          ? `${t("Interview.hide")} ▾`
                          : `${t("Interview.view_objective")} ▸`}
                      </Link>
                    </Typography>
                  )}

                  {/* Show question objective (details)  */}
                  {question.details && (
                    <Collapse
                      in={openDetailsIdx === i}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        id={`details-panel-${i}`}
                        role="region"
                        aria-labelledby={`details-toggle-${i}`}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          px: 1,
                          py: 0.75,
                          borderRadius: 1.5,
                          backgroundColor: theme.palette.background.paper,
                          boxShadow: `0 0 6px -2px ${theme.palette.primary.dark}`,
                          width: "98%",
                          mx: "auto",
                        }}
                      >
                        <Typography color="primary" variant="subtitle2">
                          {t("Interview.objective")}:
                        </Typography>
                        <Typography
                          variant="body2"
                          textAlign="justify"
                          sx={{
                            mt: 0.5,
                            px: 0.33,
                            whiteSpace: "pre-line",
                            fontStyle: "oblique",
                            fontSize: "0.92rem",
                            lineHeight: 1.25,
                          }}
                        >
                          {question.details}
                        </Typography>
                      </Box>
                    </Collapse>
                  )}

                  {/*  Input answer */}
                  <TextField
                    placeholder={t("Interview.answer")}
                    variant="standard"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={10}
                    value={question.answer ?? ""}
                    onChange={(e) => handleChange(i, "answer", e.target.value)}
                    sx={{
                      my: 1,
                      "& .MuiInputBase-input": {
                        fontSize: "0.95rem",
                      },
                    }}
                    id={`q-${i}`}
                    inputRef={(el) => (inputRefs.current[`q-${i}`] = el)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={() => setActiveIndex(i)}
                    disabled={isLoading}
                  />
                  {/* dynamic comments input */}
                  {question._showDetails && (
                    <TextField
                      label={`${t("Interview.comments")}:`}
                      id={`q-details-${i}`}
                      placeholder={t("Interview.enter_any_additional_comments")}
                      variant="outlined"
                      multiline
                      minRows={1}
                      maxRows={4}
                      fullWidth
                      sx={{
                        mt: 1.5,
                        "& .MuiInputBase-input": {
                          fontStyle: "oblique",
                          fontSize: "0.83rem",
                          lineHeight: 1.3,
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.75rem",
                        },
                      }}
                      value={question.answerDetails ?? ""}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={() => setActiveIndex(i)}
                      onChange={(e) =>
                        handleChange(i, "answerDetails", e.target.value)
                      }
                      inputRef={(el) =>
                        (inputRefs.current[`q-details-${i}`] = el)
                      }
                      disabled={isLoading}
                    />
                  )}
                  {/* --- Action buttons --- */}
                  <Fade in={activeIndex === i} timeout={200}>
                    {isMobil ? (
                      <Collapse
                        in={activeIndex === i}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Stack
                          sx={{
                            mt: 0.5,
                            pt: 0.5,
                            alignItems: "center",
                            justifyContent: "end",
                            flexFlow: "row nowrap",
                            borderTop: `1px solid ${theme.palette.blueGray50.main}`,
                          }}
                        >
                          <QuestionActions question={question} i={i} isMobil />
                        </Stack>
                      </Collapse>
                    ) : (
                      <Stack
                        spacing={0.5}
                        sx={{
                          position: "absolute",
                          borderColor: `1px solid ${theme.palette.blueGray100.main}`,
                          top: 0,
                          right: -54,
                          alignItems: "center",
                          backgroundColor: theme.palette.background.paper,
                          px: 0.5,
                          py: 0.75,
                          borderRadius: 2,
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            left: -8,
                            top: 12,
                            borderTop: "9px solid transparent",
                            borderBottom: "9px solid transparent",
                            borderRight: `9px solid ${theme.palette.background.paper}`,
                            zIndex: 1,
                          },
                        }}
                      >
                        <QuestionActions question={question} i={i} />
                      </Stack>
                    )}
                  </Fade>
                </Paper>
              ))}
            </>
          )}
        </Box>
        {/* Prevent leaving without saving changes */}
        <UnsavedChanges
          open={openCancelDialog}
          onClose={() => setOpenCancelDialog(false)}
          onCancel={() => setOpenCancelDialog(false)}
          onConfirm={() => {
            resetQuestions(); // Restore from original data
            setOpenCancelDialog(false);
            goHome();
          }}
        />
        {/* Interview closure confirmation */}
        <FinishConfirm
          open={openFinishDialog}
          onClose={() => setOpenFinishDialog(false)}
          onCancel={() => setOpenFinishDialog(false)}
          onConfirm={async () => {
            await handleSubmit("finish");
            setOpenFinishDialog(false);
          }}
          isMobil={isMobil}
          isLoading={isLoading}
        />
      </>
    );
  }
);

ApplyQnA.displayName = "ApplyQnA";
